/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Fragment } from 'preact';
import { ButtonLabelLayout } from '#UNSAFE_ButtonLabelLayout';
import { InputTypes, ToggleButtonTypes } from '#utils/UNSAFE_buttonUtils/toggle.types';
import { useId } from '#hooks/UNSAFE_useId';
import { ToggleButtonInput } from './ToggleButtonInput';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLElement>,
  | 'autofocus'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
>;

export type ToggleButtonLabelProps = IntrinsicProps & {
  /**
   * Specifies that the button separator should be rendered disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies if button is selected
   */
  isSelected?: boolean;

  /**
   * Specifies if button is toggle
   */
  isToggle?: boolean;

  /**
   * onAction is invoked when then input is pressed
   */
  onAction?: (event: Event) => void;

  /**
   * inputType specifies what type of input is needed
   */
  inputType?: ToggleButtonTypes;

  /**
   * inputName is the name of the radio or checkbox
   */
  inputName?: string;

  /**
   *  value is the value of the radio or checkbox
   */
  value?: string;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Display indicates whether only the label or all elements should be rendered by the togglebutton.
   */
  display?: 'label' | 'all' | 'icons';

  /**
   * The size indicates how large the togglebutton is rendered.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * label - togglebutton label
   */
  label?: string;
};

export const ToggleButtonLabel = ({
  isDisabled,
  isSelected,
  isToggle = true,
  inputName,
  inputType,
  value,
  startIcon,
  endIcon,
  display,
  onAction,
  size,
  label,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onTouchEnd,
  onTouchStart
}: ToggleButtonLabelProps) => {
  const isInput = inputType === 'radio' || inputType === 'checkbox';
  const labelOnly = (!startIcon && !endIcon) || display === 'label';

  // preact useId fails to be unique on storybook pages, each canvas starts from scratch
  //const childId = useId() + '-' + (isInput ? inputName : '');
  const childId = useId();

  const inputElement = isInput ? (
    <ToggleButtonInput
      isSwitch={!isToggle}
      onAction={onAction}
      isChecked={isSelected}
      inputType={inputType as InputTypes}
      inputName={inputName}
      value={value}
      inputId={childId}
      isDisabled={isDisabled}
      label={label}
      display={display}
      startIcon={startIcon}
      endIcon={endIcon}
      size={size}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
    />
  ) : undefined;

  if (!isInput) {
    // For standalone toggles, no need for labels
    return labelOnly ? (
      <Fragment>{label}</Fragment>
    ) : (
      <ButtonLabelLayout display={display} startIcon={startIcon} endIcon={endIcon} size={size}>
        {label}
      </ButtonLabelLayout>
    );
  } else {
    // For input types, must use labels and modify approach for button
    return <Fragment>{inputElement}</Fragment>;
  }
};

ToggleButtonLabel.displayName = 'ToggleButtonLabel';
